import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { AuthenticationDetails, CognitoUser, CognitoUserPool } from "amazon-cognito-identity-js";
import type { UserSummary } from "@signum/api-contracts";

interface AuthContextValue {
  user?: UserSummary;
  loading: boolean;
  signIn(email: string, password: string): Promise<{newPasswordRequired: boolean}>;
  completeNewPassword(password: string): Promise<void>;
  signOut(): void;
  accessToken(): Promise<string | undefined>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);
const poolId = import.meta.env.VITE_COGNITO_USER_POOL_ID;
const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
const localMode = !poolId || !clientId;
const pool = localMode ? undefined : new CognitoUserPool({UserPoolId: poolId, ClientId: clientId});

export function AuthProvider({children}: {children: ReactNode}) {
  const [user, setUser] = useState<UserSummary | undefined>();
  const [loading, setLoading] = useState(true);
  const [challenge, setChallenge] = useState<{user: CognitoUser} | undefined>();

  useEffect(() => {
    if (localMode) { setUser({id: "local-reviewer", email: "reviewer@local.signum", displayName: "Local reviewer"}); setLoading(false); return; }
    const current = pool!.getCurrentUser();
    if (!current) { setLoading(false); return; }
    current.getSession((error: Error | null, session: {isValid(): boolean; getIdToken(): {decodePayload(): Record<string, string>}}) => {
      if (!error && session.isValid()) {
        const claims = session.getIdToken().decodePayload();
        setUser({id: String(claims.sub), email: String(claims.email ?? claims["cognito:username"] ?? claims.sub)});
      }
      setLoading(false);
    });
  }, []);

  async function signIn(email: string, password: string) {
    if (!pool) return {newPasswordRequired: false};
    const cognitoUser = new CognitoUser({Username: email, Pool: pool});
    return new Promise<{newPasswordRequired: boolean}>((resolve, reject) => {
      cognitoUser.authenticateUser(new AuthenticationDetails({Username: email, Password: password}), {
        onSuccess(session) {
          const claims = session.getIdToken().decodePayload() as Record<string, string>;
          setUser({id: String(claims.sub), email: claims.email ?? email});
          resolve({newPasswordRequired: false});
        },
        onFailure: reject,
        newPasswordRequired() {
          // The reviewer account already received its email through AdminCreateUser.
          // Attributes supplied here are treated as updates, so resending Cognito's
          // existing email causes "Cannot modify an already provided email".
          setChallenge({user: cognitoUser});
          resolve({newPasswordRequired: true});
        },
      });
    });
  }

  async function completeNewPassword(password: string) {
    if (!challenge) throw new Error("No password challenge is active");
    return new Promise<void>((resolve, reject) => challenge.user.completeNewPasswordChallenge(password, {}, {
      onSuccess(session) {
        const claims = session.getIdToken().decodePayload() as Record<string, string>;
        setUser({id: String(claims.sub), email: String(claims.email ?? claims["cognito:username"] ?? claims.sub)});
        setChallenge(undefined);
        resolve();
      },
      onFailure: reject,
    }));
  }

  function signOut() { pool?.getCurrentUser()?.signOut(); setUser(undefined); }
  async function accessToken(): Promise<string | undefined> {
    if (!pool) return undefined;
    const current = pool.getCurrentUser();
    if (!current) return undefined;
    return new Promise((resolve, reject) => current.getSession((error: Error | null, session: {getAccessToken(): {getJwtToken(): string}}) =>
      error ? reject(error) : resolve(session.getAccessToken().getJwtToken())));
  }
  const value = useMemo(() => ({user, loading, signIn, completeNewPassword, signOut, accessToken}), [user, loading, challenge]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
